// FILE: src/components/testimonials/TestimonialsCarousel.js
//
// Rotating testimonial display for the home page's social-proof row.
// Rotation/pause/controls mirror PlatformBadgeRow (the house carousel):
//   • calm 8s auto-rotation; disabled entirely for prefers-reduced-motion,
//     while the tab is hidden, and while the pointer or keyboard focus is
//     inside the carousel
//   • manual prev/next + pagination dots always work; a manual jump pauses
//     auto-rotation ~10s so the chosen story can be read
//   • touch swipe (40px threshold)
//   • exactly one card is ever in the DOM inside a fixed-min-height stage,
//     so rotation causes no layout shift
//   • single testimonial → static card, no controls, no timer
//   • aria-live is 'off' while auto-rotating and 'polite' when the user is
//     interacting (W3C carousel pattern — announcements never spam)
//
// Data loading lives in the PARENT (ExamSelector) so the section heading and
// the two-column layout can also react to the empty/error case. This
// component is purely presentational and renders null for an empty list.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../services/telemetryService';
import TestimonialCard from './TestimonialCard';
import styles from './TestimonialsCarousel.module.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const MANUAL_PAUSE_MS = 10000;
const SWIPE_THRESHOLD_PX = 40;

export default function TestimonialsCarousel({
  testimonials,
  intervalMs = 8000,
  labelledBy,
  // How many testimonials render at once. 1 (default) = the classic single
  // rotating card. 2 = the home page's side-by-side pair (2026-08 bottom-row
  // redesign): consecutive stories share the stage, rotation advances the
  // window, controls hide when every story is already visible.
  perView = 1,
}) {
  const list = Array.isArray(testimonials) ? testimonials : [];
  const [index, setIndex] = useState(0);
  const [interacting, setInteracting] = useState(false);
  const manualPauseUntilRef = useRef(0);
  const touchStartXRef = useRef(null);
  const reduced = prefersReducedMotion();

  // Section-view telemetry: once per mount, only when stories actually show.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (list.length > 0 && !viewedRef.current) {
      viewedRef.current = true;
      trackEvent('testimonials_viewed', 'growth', { feature: 'testimonials', view: 'home' });
    }
  }, [list.length]);

  const pauseAfterManual = useCallback(() => {
    manualPauseUntilRef.current = Date.now() + MANUAL_PAUSE_MS;
    trackEvent('testimonials_nav', 'usage', { feature: 'testimonials', view: 'home' });
  }, []);
  const goTo = useCallback((i) => { setIndex(i); pauseAfterManual(); }, [pauseAfterManual]);
  const next = useCallback(() => { setIndex((i) => i + 1); pauseAfterManual(); }, [pauseAfterManual]);
  const prev = useCallback(() => { setIndex((i) => i - 1); pauseAfterManual(); }, [pauseAfterManual]);

  useEffect(() => {
    const shown = Math.max(1, Math.min(perView, list.length));
    if (reduced || interacting || list.length <= shown) return undefined;
    const id = setInterval(() => {
      if (Date.now() < manualPauseUntilRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setIndex((i) => i + 1);
    }, intervalMs);
    return () => clearInterval(id);
  }, [reduced, interacting, list.length, intervalMs, perView]);

  if (list.length === 0) return null;

  const activeIndex = ((index % list.length) + list.length) % list.length;
  const visibleCount = Math.max(1, Math.min(perView, list.length));
  const visible = Array.from(
    { length: visibleCount },
    (_, k) => list[(activeIndex + k) % list.length]
  );
  const current = visible[0];
  const showControls = list.length > visibleCount;

  const onLinkClick = (target) =>
    trackEvent('testimonials_link_click', 'growth', { feature: 'testimonials', target });

  const onTouchStart = (e) => {
    touchStartXRef.current = e.touches && e.touches[0] ? e.touches[0].clientX : null;
  };
  const onTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null || !showControls) return;
    const endX = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientX : null;
    if (endX == null) return;
    const dx = endX - startX;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    if (dx < 0) next(); else prev();
  };

  return (
    <div
      className={styles.carousel}
      role="group"
      aria-roledescription="carousel"
      aria-label={labelledBy ? undefined : 'Learner success stories'}
      aria-labelledby={labelledBy}
      data-testid="testimonials-carousel"
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      onFocus={() => setInteracting(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setInteracting(false);
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className={visibleCount > 1 ? styles.stagePair : styles.stage}
        aria-live={reduced || interacting || !showControls ? 'polite' : 'off'}
      >
        {visibleCount > 1 ? (
          visible.map((t, k) => (
            <div className={styles.pane} key={t.id || `${activeIndex}-${k}`} data-testid="testimonial-pane">
              <p className={styles.paneEyebrow}>Learner success story</p>
              <TestimonialCard testimonial={t} onLinkClick={onLinkClick} />
            </div>
          ))
        ) : (
          <TestimonialCard testimonial={current} onLinkClick={onLinkClick} />
        )}
      </div>

      {showControls && (
        <div className={styles.controls}>
          <button
            type="button"
            className={styles.nav}
            onClick={prev}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
          >
            ‹
          </button>
          <span className={styles.dots} data-testid="testimonial-dots">
            {list.map((t, i) => (
              <button
                key={t.id || i}
                type="button"
                data-testid="testimonial-dot"
                className={i === activeIndex ? `${styles.dot} ${styles.dotOn}` : styles.dot}
                aria-label={`Show testimonial ${i + 1} of ${list.length}`}
                aria-current={i === activeIndex ? 'true' : undefined}
                onClick={() => goTo(i)}
              />
            ))}
          </span>
          <button
            type="button"
            className={styles.nav}
            onClick={next}
            aria-label="Next testimonial"
            data-testid="testimonial-next"
          >
            ›
          </button>
          <span className={styles.srOnly}>
            {`Testimonial ${activeIndex + 1} of ${list.length}`}
          </span>
        </div>
      )}
    </div>
  );
}
